import {
  Button,
  ButtonColor,
  ButtonState,
} from '@delmonicos/shared-design-system-react/src/components/button/button';
import { EmailInput } from '@delmonicos/shared-design-system-react/src/components/input/email-input';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import {
  useCpmsBoRequestResetPasswordOtpMutation,
  useCpmsBoResetPasswordMutation,
} from '@app/core/service/auth.service';

import * as css from './login.module.scss';
import { OtpResendLinkComponent } from './otp-resend-link.component';
import {
  ResetPasswordOtpStepForm,
  useResetPasswordOtpStepFormValidation,
} from './reset-password-otp-step.form-validation';
import {
  ResetPasswordForm,
  useResetPasswordFormValidation,
} from './reset-password.form-validation';

interface ResendOtpComponentProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const ResendOtpComponent = ({
  onSuccess,
  onCancel,
}: ResendOtpComponentProps): React.JSX.Element => {
  const { t } = useTranslation();

  const [stepReset, setStepReset] = useState(0);

  const [requestResetPasswordOtp] = useCpmsBoRequestResetPasswordOtpMutation();
  const [resetPassword] = useCpmsBoResetPasswordMutation();

  const formValidationEmail = useResetPasswordFormValidation();
  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: formStateEmail,
    watch: watchEmail,
  } = useForm<ResetPasswordForm>({
    mode: 'onTouched',
  });

  const emailAddress = watchEmail('emailAddress');

  const formValidationOtp = useResetPasswordOtpStepFormValidation();
  const {
    register: registerOtp,
    handleSubmit: handleSubmitOtp,
    formState: formStateOtp,
  } = useForm<ResetPasswordOtpStepForm>({
    mode: 'onTouched',
  });

  const handleSendEmail = (e: React.FormEvent<HTMLFormElement>) => {
    handleSubmitEmail(requestResetPasswordOtp)(e)
      .then(() => {
        setStepReset(1);
      })
      .catch(() => {
        toast.error(t('$.login.reset-password.error'));
      });
  };
  const handleSendOtp = (e: React.FormEvent<HTMLFormElement>) => {
    handleSubmitOtp((r) => resetPassword({ ...r, emailAddress }))(e)
      .then(() => {
        toast.success(t('$.login.reset-password.success'));
        setStepReset(0);
        onSuccess();
      })
      .catch(() => {
        toast.error(t('$.login.reset-password-otp.error'));
      });
  };

  const onResendOtp = () => {
    requestResetPasswordOtp({ emailAddress })
      .then(() => {
        toast.success(t('$.login.reset-password.new-otp.success'));
      })
      .catch(() => {
        toast.error(t('$.login.reset-password.new-otp.error'));
      });
  };

  return (
    <>
      {stepReset === 0 && (
        <form onSubmit={handleSendEmail} className={css.form}>
          <EmailInput
            label={t('$.login.reset-password.email')}
            required
            errorMessage={formStateEmail.errors?.emailAddress?.message}
            {...registerEmail('emailAddress', formValidationEmail.emailAddress)}
          />
          <Stack gap={GapEnum.M} className={css.actions}>
            <Button color={ButtonColor.Secondary} onClick={onCancel}>
              {t('$.login.reset-password.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!formStateEmail.isValid}
              state={
                formStateEmail.isValid
                  ? ButtonState.Default
                  : ButtonState.Disabled
              }>
              {t('$.login.reset-password.next')}
            </Button>
          </Stack>
        </form>
      )}
      {stepReset === 1 && (
        <form onSubmit={handleSendOtp} className={css.form}>
          <TextInput
            label={t('$.login.reset-password.otp')}
            required
            errorMessage={formStateOtp.errors?.code?.message}
            {...registerOtp('code', formValidationOtp.code)}
          />
          <div className={css.otpResendLink}>
            <OtpResendLinkComponent onResendOtp={onResendOtp} />
          </div>
          <Stack gap={GapEnum.M} className={css.actions}>
            <Button color={ButtonColor.Secondary} onClick={onCancel}>
              {t('$.login.reset-password.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!formStateOtp.isValid}
              state={
                formStateOtp.isValid
                  ? ButtonState.Default
                  : ButtonState.Disabled
              }>
              {t('$.login.reset-password.validate')}
            </Button>
          </Stack>
        </form>
      )}
    </>
  );
};
